import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiFailure, api, type AdminRecording, type Pipeline, type Version, type RecordingState } from '../api.ts';
import { ConfirmDanger } from '../ConfirmDanger.tsx';
import { formatDuration } from '../duration.ts';
import { reviewChip, useReviewFacts } from '../review.ts';

/**
 * WHAT IS IN THIS PROJECT, and how far through the pipeline each one is.
 *
 * TWO COLUMNS, TWO DIFFERENT QUESTIONS. `Status` is where the MACHINE left the
 * recording; `Review` is what people have since done with it — nobody yet, two
 * submitted, or a truth picked. Folding them into one column was the obvious
 * first draft and it loses the row that matters most: a `review` recording with
 * two arrangements waiting is not in the same state as a `review` recording
 * nobody has opened, and the pipeline has no word for the difference. The rule
 * behind each chip lives in `review.ts`, so this page renders a decision rather
 * than making one.
 *
 * THE POLL RUNS ONLY WHILE SOMETHING IS MOVING. `queued` and `processing` are
 * the two states that change without anybody touching the page; everything
 * else changes only when a person acts, and a person acting already triggers a
 * refresh. Polling a page of finished rows every five seconds would be a
 * request per five seconds per open tab, for ever, to be told nothing.
 *
 * `setTimeout` CHAINED FROM THE RESPONSE, not `setInterval`. An interval fires
 * on a schedule whether or not the previous request came back; on a slow link
 * that stacks requests until they overlap, and the list then flickers between
 * two answers arriving out of order. Waiting for the answer before scheduling
 * the next question makes the period "five seconds of quiet", which is what
 * was actually meant.
 */

const POLL_MS = 5000;

/** The two states a worker moves out of on its own. */
const MOVING: ReadonlySet<RecordingState> = new Set<RecordingState>(['queued', 'processing']);

const STATUS_LABEL: Record<RecordingState, string> = {
  uploaded: 'uploaded',
  queued: 'queued',
  processing: 'processing',
  review: 'review',
  done: 'done',
  failed: 'failed',
};

export function Recordings({
  token,
  projectId,
  /** Bumped by the uploader so a finished upload shows up without waiting for
   *  the next poll — and without the uploader needing to know how this list
   *  loads. */
  refreshKey,
  /** Where a recording's name links to — the project page passes its own
   *  child route (`#/projects/<id>/r/`). */
  linkBase,
  onSignedOut,
}: {
  token: string;
  projectId: string;
  refreshKey: number;
  linkBase: string;
  onSignedOut: () => void;
}): JSX.Element {
  const [rows, setRows] = useState<AdminRecording[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  /** The recording a Delete click is asking about; null when nobody asked. */
  const [condemned, setCondemned] = useState<AdminRecording | null>(null);
  /** The recording being renamed/described; null when nobody is. */
  const [editing, setEditing] = useState<AdminRecording | null>(null);

  /**
   * The version lookups behind the Review column, forgotten and asked again
   * whenever a person presses Refresh — a submission that lands while the page
   * is open changes nothing the POLL can see, because the poll reads recordings
   * and a submission touches a version. The project is folded in so switching
   * projects starts from an empty map rather than from the last one's answers.
   *
   * The uploader's `refreshKey` is deliberately NOT in here. A new recording
   * appears in `rows` and gets its own lookup on the way past; throwing away
   * sixty answered lookups because a seventh file was queued would be paying
   * for a question nobody asked.
   */
  const [factsKey, setFactsKey] = useState(0);
  const reviewFacts = useReviewFacts(token, rows, `${projectId}:${factsKey}`);

  /*
   * The timer and the in-flight request live in refs, not state: nothing
   * renders differently because of them, and putting them in state would make
   * every poll a re-render of the whole table.
   */
  const timer = useRef<number | null>(null);
  const inFlight = useRef<AbortController | null>(null);
  const onSignedOutRef = useRef(onSignedOut);
  onSignedOutRef.current = onSignedOut;

  /*
   * The rows as the POLL sees them. The effect below needs to know whether
   * anything is still moving, and reading `rows` directly would put it in the
   * dependency list — restarting the poll on every response, which is a poll
   * with no interval at all.
   */
  const rowsRef = useRef<AdminRecording[] | null>(null);
  rowsRef.current = rows;

  /** The pipeline templates, for the run modal's dropdown. */
  const [pipes, setPipes] = useState<Pipeline[]>([]);
  /** The row whose "Create a new run" modal is open. */
  const [running, setRunning] = useState<AdminRecording | null>(null);
  /** The row whose versions modal is open. */
  const [versionsOf, setVersionsOf] = useState<AdminRecording | null>(null);

  const load = useCallback(
    async (quiet: boolean): Promise<AdminRecording[] | null> => {
      inFlight.current?.abort();
      const controller = new AbortController();
      inFlight.current = controller;
      if (!quiet) setRefreshing(true);

      try {
        const { recordings } = await api.recordings(token, projectId, controller.signal);
        setRows(recordings);
        setError(null);
        return recordings;
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return null;
        if (err instanceof ApiFailure && err.status === 401) {
          onSignedOutRef.current();
          return null;
        }
        /*
         * A failed POLL keeps the rows it already has and says so quietly. The
         * list going blank because one request timed out on a train would be a
         * worse answer than a slightly stale list with a warning over it.
         */
        setError(err instanceof ApiFailure ? err.message : 'Could not load recordings.');
        return null;
      } finally {
        if (inFlight.current === controller) inFlight.current = null;
        if (!quiet) setRefreshing(false);
      }
    },
    [token, projectId],
  );

  useEffect(() => {
    let cancelled = false;

    const tick = async (quiet: boolean): Promise<void> => {
      const fresh = await load(quiet);
      if (cancelled) return;
      // Schedule from the ANSWER. `fresh === null` means the request failed or
      // was superseded; keep polling in that case only if we already know
      // something was moving, so a transient failure does not stop the page
      // updating for ever.
      const list = fresh ?? rowsRef.current;
      if (list && list.some((r) => MOVING.has(r.status))) {
        timer.current = window.setTimeout(() => void tick(true), POLL_MS);
      }
    };

    void tick(false);

    return () => {
      cancelled = true;
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = null;
      inFlight.current?.abort();
    };
  }, [load, refreshKey]);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listPipelines(token, controller.signal)
      .then((res) => setPipes(res.pipelines))
      .catch(() => {});
    return () => controller.abort();
  }, [token, refreshKey]);

  const moving = rows?.filter((r) => MOVING.has(r.status)).length ?? 0;

  return (
    <section className="card">
      <div className="card-head">
        <h2>Recordings</h2>
        <span className="topbar-spacer" />
        {moving > 0 && (
          <span className="hint-tight num" style={{ margin: 0 }}>
            {moving} in flight · refreshing every {POLL_MS / 1000}s
          </span>
        )}
        <button
          className="btn-link"
          type="button"
          onClick={() => {
            setFactsKey((k) => k + 1);
            void load(false);
          }}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="notice notice-warn" role="status">
          {error}
        </div>
      )}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Review</th>
              <th>Length</th>
              <th>Versions</th>
              <th aria-label="Run" />
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr>
                <td className="empty" colSpan={7}>
                  Loading…
                </td>
              </tr>
            )}
            {rows !== null && rows.length === 0 && (
              <tr>
                <td className="empty" colSpan={7}>
                  No recordings in this project yet. Audio comes in through the uploader above.
                </td>
              </tr>
            )}
            {rows?.map((r) => {
              const chip = reviewChip(r, reviewFacts.get(r.id));
              return (
                <tr key={r.id}>
                  <td className="cell-name">
                    <a
                      className="cell-clip"
                      href={`${linkBase}${encodeURIComponent(r.id)}`}
                      title={r.description ?? r.name}
                    >
                      {r.name}
                    </a>
                    {r.sourceTaskId && (
                      <span className="pill pill-processing" style={{ marginInlineStart: 6 }}>
                        task
                      </span>
                    )}
                    {(r.flow === 'staged' || r.flow === 'extract' || r.flow === 'sshwl') && (
                      <span
                        className={`pill ${r.stage === 'done' ? 'pill-done' : 'pill-review'}`}
                        style={{ marginInlineStart: 6 }}
                        title={
                          r.flow === 'extract'
                            ? 'Extract: pull each kind from the pool — silences, noises, umms, words, seams, then leftovers.'
                            : 'Staged: one kind at a time — silences, noises, umms, unclear, then words.'
                        }
                      >
                        {r.stage === 'done'
                          ? `${r.flow} · done`
                          : {
                              silence: 'silences',
                              noise: 'noises',
                              filler: 'umms',
                              unclear: 'unclear',
                              word: 'words',
                              carve: 'seams',
                              remnant: 'leftovers',
                            }[r.stage ?? 'silence'] ?? 'silences'}
                      </span>
                    )}
                    {r.flow === 'anchored' && (
                      <span
                        className={`pill ${r.anchorsSettledAt ? 'pill-done' : 'pill-review'}`}
                        style={{ marginInlineStart: 6 }}
                        title={
                          r.anchorsSettledAt
                            ? 'Silence-first: anchors settled; islands being edited.'
                            : 'Silence-first: waiting for the silences to be settled on a phone.'
                        }
                      >
                        silences
                      </span>
                    )}
                  </td>
                  <td>
                    <span
                      className={`pill pill-${r.status}`}
                      title={r.status === 'failed' ? (r.error ?? undefined) : undefined}
                    >
                      {STATUS_LABEL[r.status]}
                    </span>
                  </td>
                  <td>
                    {chip ? (
                      <span className={`pill pill-${chip.tone}`} title={chip.title}>
                        {chip.label}
                      </span>
                    ) : (
                      <span className="cell-dim">—</span>
                    )}
                  </td>
                  <td className="cell-dim num">{formatDuration(r.durationSec)}</td>
                  <td>
                    <button
                      className="btn-link"
                      type="button"
                      disabled={r.versionCount === 0}
                      title="Every arrangement — the AI's runs and people's edits"
                      onClick={() => setVersionsOf(r)}
                    >
                      {r.versionCount} version{r.versionCount === 1 ? '' : 's'}
                    </button>
                  </td>
                  <td>
                    <button
                      className="btn-quiet"
                      type="button"
                      style={{ width: 'auto', padding: '4px 12px', fontSize: 13, whiteSpace: 'nowrap' }}
                      disabled={r.status === 'queued' || r.status === 'processing'}
                      onClick={() => setRunning(r)}
                    >
                      {r.status === 'queued' || r.status === 'processing'
                        ? 'Running…'
                        : 'Create a new run'}
                    </button>
                  </td>
                  <td>
                    <div className="btn-row" style={{ flexWrap: 'nowrap' }}>
                      <button className="btn-link" type="button" onClick={() => setEditing(r)}>
                        Edit
                      </button>
                      <button
                        className="btn-link"
                        type="button"
                        style={{ color: 'var(--danger)' }}
                        disabled={r.status === 'queued' || r.status === 'processing'}
                        title={
                          r.status === 'queued' || r.status === 'processing'
                            ? 'Wait for the run to finish first'
                            : 'Permanently delete this recording'
                        }
                        onClick={() => setCondemned(r)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {condemned && (
        <ConfirmDelete
          token={token}
          recording={condemned}
          onDone={(deleted) => {
            setCondemned(null);
            if (deleted) {
              setFactsKey((k) => k + 1);
              void load(false);
            }
          }}
        />
      )}

      {editing && (
        <EditRecording
          token={token}
          recording={editing}
          onDone={(changed) => {
            setEditing(null);
            if (changed) void load(false);
          }}
        />
      )}

      {running && (
        <RunModal
          token={token}
          recording={running}
          pipes={pipes}
          onDone={(ran) => {
            setRunning(null);
            if (ran) void load(false);
          }}
        />
      )}

      {versionsOf && (
        <VersionsModal
          token={token}
          recording={versionsOf}
          linkBase={linkBase}
          onDone={() => setVersionsOf(null)}
          onChanged={() => {
            setFactsKey((k) => k + 1);
            void load(true);
          }}
        />
      )}
    </section>
  );
}

/**
 * CREATE A NEW RUN — the owner's modal, exactly as asked: "I click on create
 * a new run I see a modal to select those things, then click on run, the
 * versions will be bumped up +1." Those things: how humans verify it (the
 * flow), and which pipeline the AI uses. Every run lands as its own version.
 */
function RunModal({
  token,
  recording,
  pipes,
  onDone,
}: {
  token: string;
  recording: AdminRecording;
  pipes: Pipeline[];
  onDone: (ran: boolean) => void;
}): JSX.Element {
  const [flow, setFlow] = useState<'direct' | 'anchored' | 'staged' | 'extract' | 'sshwl'>(
    recording.flow,
  );
  /** The chosen template's id. Empty until the person picks — every run
   *  names a pipeline, nothing is preselected ("no default, no champion"). */
  const [pipelineId, setPipelineId] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const run = async (): Promise<void> => {
    if (busy || !pipelineId) return;
    setBusy(true);
    setFailed(null);
    try {
      if (flow !== recording.flow) {
        await api.patchRecording(token, recording.id, { flow });
      }
      await api.runRecording(token, recording.id, pipelineId);
      onDone(true);
    } catch (err) {
      setFailed(err instanceof ApiFailure ? err.message : 'Could not start the run.');
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={() => !busy && onDone(false)}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="run-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="run-modal-title">New AI run</h2>
        <p className="hint">
          <strong>{recording.name}</strong> — the run lands as its own version, next to the
          existing {recording.versionCount === 1 ? 'one' : String(recording.versionCount)}.
        </p>
        <label className="field">
          <span className="field-label">How people verify it</span>
          <select
            value={flow}
            onChange={(e) =>
              setFlow(e.target.value as 'direct' | 'anchored' | 'staged' | 'extract' | 'sshwl')
            }
            disabled={busy}
          >
            <option value="direct">Direct — the whole timeline as parts</option>
            <option value="anchored">Silences first — settle anchors, then islands</option>
            <option value="staged">Staged — silences, noises, umms, unclear, then words</option>
            <option value="extract">
              Extract — pull each kind from the pool; seams and leftovers last
            </option>
            <option value="sshwl">
              SSHWL — silences, then each sound on its own, hesitations, words, leftovers last
            </option>
          </select>
        </label>
        <label className="field">
          <span className="field-label">Pipeline</span>
          <select
            value={pipelineId}
            onChange={(e) => setPipelineId(e.target.value)}
            disabled={busy || pipes.length === 0}
          >
            <option value="">{pipes.length === 0 ? 'No pipelines yet' : 'Pick a pipeline…'}</option>
            {pipes.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        {pipes.length === 0 && (
          <p className="hint">No pipelines yet — create one in Setup first.</p>
        )}
        {failed && (
          <div className="notice notice-warn" role="status">
            {failed}
          </div>
        )}
        <div className="btn-row">
          <button className="btn-quiet" type="button" disabled={busy} onClick={() => onDone(false)}>
            Cancel
          </button>
          <button
            className="btn-primary"
            type="button"
            style={{ width: 'auto' }}
            disabled={busy || !pipelineId}
            onClick={() => void run()}
          >
            {busy ? 'Starting…' : 'Run'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** `Aug 21, 14:05` — enough to tell two runs of the same pipeline apart. */
function versionWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * THE VERSIONS DOOR — click the count, see every arrangement: the AI's runs
 * (labeled by the NAME of the pipeline that made them) and people's edits.
 * One click opens the player on that version alone; "View all together"
 * opens the first few side by side; Delete removes an arrangement for ever.
 */
function VersionsModal({
  token,
  recording,
  linkBase,
  onDone,
  onChanged,
}: {
  token: string;
  recording: AdminRecording;
  linkBase: string;
  onDone: () => void;
  /** A version was deleted — the row behind this modal shows a count. */
  onChanged: () => void;
}): JSX.Element {
  const [versions, setVersions] = useState<Version[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  /** The version a Delete click is asking about; null when nobody asked. */
  const [condemned, setCondemned] = useState<Version | null>(null);
  const [bump, setBump] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    api
      .versions(token, recording.id, controller.signal)
      .then((res) => {
        setVersions(res.versions);
        setFailed(null);
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setFailed(err instanceof ApiFailure ? err.message : 'Could not load the versions.');
      });
    return () => controller.abort();
  }, [token, recording.id, bump]);

  const go = (lane: string): void => {
    window.location.hash = `${linkBase}${recording.id}/v/${lane}`;
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onDone}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="versions-modal-title"
        /* Wider than the base modal: a row is label + pill + author·clips·date
           + Delete, and 460px starved the label on pill-carrying rows. */
        style={{ maxWidth: 620 }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="versions-modal-title">Versions of {recording.name}</h2>
        {failed && (
          <div className="notice notice-warn" role="status">
            {failed}
          </div>
        )}
        {versions === null && !failed && <p className="hint">Loading…</p>}
        {versions !== null && versions.length === 0 && (
          <p className="hint">No versions left — run the AI again from the row.</p>
        )}
        {versions !== null && (
          <div className="version-list">
            {versions.map((v) => (
              <div key={v.id} className="version-line">
                <button
                  className="version-row"
                  type="button"
                  onClick={() => go(v.id)}
                  title="Open the player on this version"
                >
                  <span className="version-label">{v.label}</span>
                  {v.status === 'main' && <span className="pill pill-done">truth so far</span>}
                  <span className="hint-tight num" style={{ margin: 0 }}>
                    {v.authorName ?? 'the machine'} · {v.segmentCount} clips · {versionWhen(v.createdAt)}
                  </span>
                </button>
                <button
                  className="btn-link btn-link-danger"
                  type="button"
                  title="Permanently delete this version"
                  onClick={() => setCondemned(v)}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="btn-row">
          <button className="btn-quiet" type="button" onClick={onDone}>
            Close
          </button>
          <button
            className="btn-primary"
            type="button"
            style={{ width: 'auto' }}
            disabled={!versions || versions.length === 0}
            onClick={() => go('all')}
          >
            View all together
          </button>
        </div>
        {condemned && (
          <ConfirmDanger
            title="Delete this version for ever?"
            warning={
              <>
                <strong>{condemned.label}</strong> ({condemned.segmentCount} clips
                {condemned.status === 'main' ? ', the truth so far' : ''}) — its clips, verdicts
                and votes go, for everyone, permanently.
                {condemned.status === 'main'
                  ? ' The oldest remaining version becomes the truth so far.'
                  : ''}{' '}
                The audio itself stays.
              </>
            }
            phrase="delete"
            actionLabel="Delete the version"
            busyLabel="Deleting…"
            onRun={() => api.deleteVersion(token, condemned.id).then(() => undefined)}
            onDone={(done) => {
              setCondemned(null);
              if (done) {
                setBump((k) => k + 1);
                onChanged();
              }
            }}
          />
        )}
      </div>
    </div>
  );
}

/**
 * RENAME AND DESCRIBE — "give it a name by clicking edit on the default name
 * it came with". The name arrives as a filename; this is where it becomes a
 * name for people, and the description is the admin's words about the audio.
 */
function EditRecording({
  token,
  recording,
  onDone,
}: {
  token: string;
  recording: AdminRecording;
  onDone: (changed: boolean) => void;
}): JSX.Element {
  const [name, setName] = useState(recording.name);
  const [description, setDescription] = useState(recording.description ?? '');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  async function save(): Promise<void> {
    if (busy || !name.trim()) return;
    setBusy(true);
    setFailed(null);
    try {
      await api.patchRecording(token, recording.id, {
        name: name.trim(),
        description: description.trim(),
      });
      onDone(true);
    } catch (err) {
      setFailed(err instanceof ApiFailure ? err.message : 'Could not save it. Try again.');
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={() => !busy && onDone(false)}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-rec-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="edit-rec-title">Name this recording</h2>
        <label className="field">
          <span className="field-label">Name</span>
          <input
            className="input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            disabled={busy}
          />
        </label>
        <label className="field">
          <span className="field-label">Description</span>
          <input
            className="input"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this audio is, where it came from"
            disabled={busy}
          />
        </label>
        {failed && (
          <div className="notice notice-warn" role="status">
            {failed}
          </div>
        )}
        <div className="btn-row">
          <button className="btn-quiet" type="button" disabled={busy} onClick={() => onDone(false)}>
            Cancel
          </button>
          <button
            className="btn-primary"
            type="button"
            style={{ width: 'auto' }}
            disabled={busy || !name.trim()}
            onClick={() => void save()}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * THE POINT OF NO RETURN, spelled out and typed back.
 *
 * Deleting a recording removes its audio, every version, every verdict and
 * every clip — for everyone, permanently. So the dialog follows the
 * `ConfirmPromote` anatomy (the one destructive-confirm pattern this admin
 * has) and adds the harder proof: the person types the recording's exact
 * name, and the server checks the same string again, so no click-path can
 * shortcut it. Born of the owner meeting twin cards they could not tell
 * apart or remove: "I want it gone, I want a clean slate."
 */
function ConfirmDelete({
  token,
  recording,
  onDone,
}: {
  token: string;
  recording: AdminRecording;
  onDone: (deleted: boolean) => void;
}): JSX.Element {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const match = typed === 'delete';

  const doDelete = async (): Promise<void> => {
    if (!match || busy) return;
    setBusy(true);
    setFailed(null);
    try {
      await api.deleteRecording(token, recording.id, typed);
      onDone(true);
    } catch (err) {
      setFailed(err instanceof ApiFailure ? err.message : 'Could not delete it. Try again.');
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={() => !busy && onDone(false)}>
      <div
        className="modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-delete-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="confirm-delete-title">Delete this recording for ever?</h2>
        <p className="hint">
          <strong>{recording.name}</strong> — the audio, every version, every verdict and every
          clip go, for everyone, permanently. There is no undo and no trash.
        </p>
        <p className="hint">
          Type <strong>delete</strong> to arm the button:
        </p>
        <input
          className="input"
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="delete"
          disabled={busy}
          autoFocus
        />
        {failed && (
          <div className="notice notice-warn" role="status">
            {failed}
          </div>
        )}
        <div className="btn-row">
          <button className="btn-quiet" type="button" disabled={busy} onClick={() => onDone(false)}>
            Cancel
          </button>
          <button
            className="btn-danger"
            type="button"
            style={{ width: 'auto' }}
            disabled={!match || busy}
            onClick={() => void doDelete()}
          >
            {busy ? 'Deleting…' : 'Delete for ever'}
          </button>
        </div>
      </div>
    </div>
  );
}
